import React, { useState } from 'react';
import { getFinancialTooltip, countryData } from '../../data/countryData';
import { useLanguage } from '../../context/LanguageContext';

/**
 * FinancialTooltip component for displaying country-specific financial information
 * @param {Object} props - Component props
 * @param {string} props.countryCode - Selected country code
 * @param {boolean} props.visible - Whether tooltip is visible
 * @param {Function} props.onToggle - Toggle visibility callback
 */
const FinancialTooltip = ({ countryCode, visible = false, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(visible);
  const tooltipData = getFinancialTooltip(countryCode);
  const { t } = useLanguage();

  if (!tooltipData) {
    return null;
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (onToggle) {
      onToggle(!isExpanded);
    }
  };

  const countryName = countryData[countryCode]?.name;
  const titleTemplate = t('wizard.financial.title', 'Financial Information for {{country}}');
  const subtitle = t('wizard.financial.subtitle', 'Important cost information and financial requirements');

  return (
    <div className="financial-tooltip">
      {/* Toggle Button */}
      <button
        onClick={handleToggle}
        className="flex items-center justify-between w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-expanded={isExpanded}
        aria-controls="financial-tooltip-content"
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-blue-900">
              {titleTemplate.replace('{{country}}', countryName || '')}
            </h3>
            <p className="text-sm text-blue-700">
              {subtitle}
            </p>
          </div>
        </div>

        <div className="flex-shrink-0">
          <svg
            className={`w-5 h-5 text-blue-500 transform transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {/* Tooltip Content */}
      {isExpanded && (
        <div
          id="financial-tooltip-content"
          className="mt-4 p-6 bg-white border border-blue-200 rounded-lg shadow-lg"
        >
          <div className="space-y-4">
            {Object.entries(tooltipData).map(([key, value]) => (
              <FinancialInfoItem
                key={key}
                title={formatInfoTitle(key, t)}
                value={value}
                icon={getInfoIcon(key)}
              />
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-blue-100">
            <p className="text-sm text-gray-600 text-center">
              💡 <strong>{t('wizard.financial.tipTitle', 'Tip:')}</strong> {t('wizard.financial.tipBody', 'These costs are estimates. Actual amounts may vary based on your specific situation and current exchange rates.')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * FinancialInfoItem component for individual financial information items
 */
const FinancialInfoItem = ({ title, value, icon: Icon }) => {
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-700">{value}</p>
      </div>
    </div>
  );
};

/**
 * Helper function to format info titles
 */
const formatInfoTitle = (key, t) => {
  const titles = {
    blockedAccount: t('wizard.financial.titles.blockedAccount', 'Blocked Account Requirement'),
    sponsorSavings: t('wizard.financial.titles.sponsorSavings', 'Sponsorship Benefits'),
    tuition: t('wizard.financial.titles.tuition', 'Tuition Fees'),
    loanOption: t('wizard.financial.titles.loanOption', 'Payment Options'),
    costs: t('wizard.financial.titles.costs', 'Estimated Costs'),
    workPermit: t('wizard.financial.titles.workPermit', 'Work Rights'),
    workRights: t('wizard.financial.titles.workRights', 'Work Rights'),
    postStudy: t('wizard.financial.titles.postStudy', 'Post-Study Opportunities'),
    guidance: t('wizard.financial.titles.guidance', 'Our Guidance'),
    comparison: t('wizard.financial.titles.comparison', 'Comparison Service'),
    support: t('wizard.financial.titles.support', 'Support Available'),
    budgetRange: t('wizard.financial.titles.budgetRange', 'Budget Planning')
  };

  if (titles[key]) {
    return titles[key];
  }

  const fallback = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
  return t(`wizard.financial.titles.${key}`, fallback);
};

/**
 * Helper function to get appropriate icons for different info types
 */
const getInfoIcon = (key) => {
  const icons = {
    blockedAccount: (props) => (
      <svg {...props} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    ),
    sponsorSavings: (props) => (
      <svg {...props} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 100 4h2a2 2 0 100 4h2a1 1 0 100 2 2 2 0 01-2 2H6a2 2 0 01-2-2V5z" clipRule="evenodd" />
      </svg>
    ),
    tuition: (props) => (
      <svg {...props} fill="currentColor" viewBox="0 0 20 20">
        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
        <path fillRule="evenodd" d="M3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" clipRule="evenodd" />
      </svg>
    ),
    costs: (props) => (
      <svg {...props} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1zm1-4a1 1 0 100 2h.01a1 1 0 100-2H7zm2 1a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm4-2a1 1 0 100 2h.01a1 1 0 100-2H13z" clipRule="evenodd" />
      </svg>
    ),
    workRights: (props) => (
      <svg {...props} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
        <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
      </svg>
    ),
    postStudy: (props) => (
      <svg {...props} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
      </svg>
    ),
    guidance: (props) => (
      <svg {...props} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    )
  };

  return icons[key] || icons.guidance;
};

export default FinancialTooltip;
